import type { ApplicationSettingThemeColor } from '@lighthouse/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** 与 ColorPicker 共用的 localStorage key */
const RECENT_COLORS_STORAGE_KEY = 'color-picker-recent-colors'
/** 最大最近颜色数量（与 ColorPicker 保持一致） */
const MAX_RECENT_COLORS = 8

export interface FillPickerContextValue {
    palettes: ApplicationSettingThemeColor[]
    /** 最近使用的颜色列表（最多8个，去重） */
    recentColors: string[]
    /** 设置待提交的颜色（不立即保存，等待组件卸载时自动提交） */
    setPendingColor: (color: string) => void
    /** 提交待提交的颜色到最近使用列表 */
    commitPendingColor: () => void
}

const FillPickerContext = React.createContext<FillPickerContextValue>({
    palettes: [],
    recentColors: [],
    setPendingColor: () => {},
    commitPendingColor: () => {}
})

const FillPickerProvider = FillPickerContext.Provider
export const useFillPickerContext = () => React.useContext(FillPickerContext)

/** 从 localStorage 获取最近使用的颜色 */
export function getRecentColorsFromStorage(): string[] {
    try {
        return JSON.parse(localStorage.getItem(RECENT_COLORS_STORAGE_KEY) || '[]')
    } catch {
        return []
    }
}

/** 保存最近使用的颜色到 localStorage */
export function saveRecentColorsToStorage(colors: string[]): void {
    localStorage.setItem(RECENT_COLORS_STORAGE_KEY, JSON.stringify(colors))
}

interface FillPickerProviderWithStorageProps {
    palettes: ApplicationSettingThemeColor[]
    children: React.ReactNode
}

/**
 * 封装了 localStorage 持久化逻辑的 FillPickerProvider
 * - 自动从 localStorage 读取最近使用的颜色
 * - 使用与 ColorPicker 相同的 key，实现共用
 * - 颜色在 FillPickerCore 卸载时自动保存（popover 关闭时）
 */
export function FillPickerProviderWithStorage({ palettes, children }: FillPickerProviderWithStorageProps) {
    // 从 localStorage 初始化最近颜色
    const [recentColors, setRecentColors] = useState<string[]>(() => getRecentColorsFromStorage())

    // 待提交的颜色
    const pendingColorRef = useRef<string | null>(null)

    // 监听 storage 事件，同步其他标签页的变更
    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (e.key === RECENT_COLORS_STORAGE_KEY && e.newValue) {
                try {
                    setRecentColors(JSON.parse(e.newValue))
                } catch {
                    // ignore
                }
            }
        }
        window.addEventListener('storage', handleStorage)
        return () => window.removeEventListener('storage', handleStorage)
    }, [])

    // 设置待提交的颜色（不立即保存）
    const setPendingColor = useCallback((color: string) => {
        pendingColorRef.current = color
    }, [])

    // 提交待提交的颜色到最近使用列表
    const commitPendingColor = useCallback(() => {
        const color = pendingColorRef.current
        if (!color) {
            return
        }

        pendingColorRef.current = null

        setRecentColors(prev => {
            const normalizedColor = color.toLowerCase()
            const filtered = prev.filter(c => c.toLowerCase() !== normalizedColor)
            const newColors = [color, ...filtered].slice(0, MAX_RECENT_COLORS)

            if (newColors.length === prev.length && newColors.every((c, i) => c === prev[i])) {
                return prev
            }

            saveRecentColorsToStorage(newColors)
            return newColors
        })
    }, [])

    const contextValue = useMemo<FillPickerContextValue>(
        () => ({
            palettes,
            recentColors,
            setPendingColor,
            commitPendingColor
        }),
        [palettes, recentColors, setPendingColor, commitPendingColor]
    )

    return <FillPickerProvider value={contextValue}>{children}</FillPickerProvider>
}
